import { BaseDownloadFileUsingPost, BaseGet, BasePost } from './BaseData';
import { appSettings } from '../AppSettings';
import { Dictionary } from 'lodash';
import { GridColType } from '@mui/x-data-grid-pro';

export type SelectableFieldType = 'text' | 'date' | 'dateTime' | 'number';

export const SelectableFieldGridColTypeMapper: Dictionary<GridColType> = {
  text: 'string',
  date: 'date',
  dateTime: 'dateTime',
  number: 'number',
};

export interface SelectableEntity {
  name: string;
  displayName: string;
  fields: ReadonlyArray<SelectableField>;
}

export interface SelectableField {
  columnName: string;
  columnDisplayName: string;
  type: SelectableFieldType;
  key: string;
  fullDisplayName: string;
}

export interface SelectableCandidate {
  sampleId: string;
  entryId: string;
  researchProjectId?: string;
}

export interface GetFieldsPayload {
  entities: ReadonlyArray<SelectableEntity>;
  candidates: ReadonlyArray<SelectableCandidate>;
  researchProjectId?: string;
}

export type SelectedData = {
  data: Dictionary<Dictionary<string | undefined | null>>;
  columns: ReadonlyArray<SelectableField>;
  entries: ReadonlyArray<SelectedEntry>;
};

export type SelectedEntry = {
  entryId: string;
  sampleId: string;
  researchProjectId?: string;
};

export async function GetAllSelectableFieldsForResearchProject(
  researchProjectId: string,
  accessToken?: string
): Promise<ReadonlyArray<SelectableEntity>> {
  return await BaseGet(
    `${appSettings.api.endpoint}/api/v2/SampleTrackingFieldSelection/SelectableFields/${researchProjectId}`,
    accessToken
  );
}

export async function SelectFields(data: GetFieldsPayload, accessToken?: string): Promise<SelectedData> {
  return await BasePost(`${appSettings.api.endpoint}/api/v2/SampleTrackingFieldSelection/Select`, data, accessToken);
}

// Group candidates by project and fetch data for each project
export async function SelectFieldsGroupedByProject(
  candidates: ReadonlyArray<SelectableCandidate>,
  entities: ReadonlyArray<SelectableEntity>,
  currentProjectId: string,
  accessToken?: string
): Promise<SelectedData> {
  const grouped: { [projectId: string]: SelectableCandidate[] } = {};
  candidates.forEach(c => {
    const projectId = c.researchProjectId ?? currentProjectId;
    (grouped[projectId] = grouped[projectId] || []).push(c);
  });
  const projectIds = Object.keys(grouped);

  const results = await Promise.all(
    projectIds.map(async projectId => ({
      projectId,
      response: await SelectFields(
        { entities, candidates: grouped[projectId], researchProjectId: projectId },
        accessToken
      ),
    }))
  );

  const data: Dictionary<Dictionary<string | undefined | null>> = {};
  const columns: SelectableField[] = [];
  const entries: SelectedEntry[] = [];

  results.forEach(({ projectId, response }) => {
    Object.assign(data, response.data);
    entries.push(...response.entries.map(e => ({ ...e, researchProjectId: projectId })));
    response.columns.forEach(col => {
      if (!columns.find(c => c.key === col.key)) columns.push(col);
    });
  });

  return { data, columns, entries };
}

export async function ExportTransitionManifest(data: GetFieldsPayload, accessToken?: string): Promise<void> {
  await BaseDownloadFileUsingPost(
    `${appSettings.api.endpoint}/api/v2/SampleTrackingFieldSelection/Export`,
    data,
    accessToken
  );
}
